import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';
import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';

import { toSortableTimestamp } from '~/services/TimeService';

import { formatSyncedDay, formatSyncedMoney } from '../Assets/utils';

import AccountListPageHeader from './AccountListPageHeader';
import { toSortableAmount } from './accountRelatedModel';
import { useAccountAssets } from './useAccountAssets';

const sharedOpts: Partial<GridColDef<OpportunityRoomAsset>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const numericOpts: Partial<GridColDef<OpportunityRoomAsset>> = {
  ...sharedOpts,
  type: 'number',
  align: 'left',
  headerAlign: 'left',
};

const COLUMNS: Array<GridColDef<OpportunityRoomAsset>> = [
  {
    field: 'name',
    headerName: 'Asset Name',
    flex: 2,
    minWidth: 240,
    ...sharedOpts,
  },
  {
    field: 'inclusion_description__c',
    headerName: 'Inclusion',
    flex: 2,
    minWidth: 200,
    ...sharedOpts,
  },
  {
    field: 'rrp__c',
    headerName: 'RRP',
    flex: 1,
    minWidth: 120,
    valueGetter: (_value, row) => toSortableAmount(row.rrp__c),
    renderCell: ({ row }) => formatSyncedMoney(row.currencyisocode, row.rrp__c) ?? '—',
    ...numericOpts,
  },
  {
    field: 'rack_rate__c',
    headerName: 'Rack Rate',
    flex: 1,
    minWidth: 120,
    valueGetter: (_value, row) => toSortableAmount(row.rack_rate__c),
    renderCell: ({ row }) => formatSyncedMoney(row.currencyisocode, row.rack_rate__c) ?? '—',
    ...numericOpts,
  },
  {
    field: 'recordtype__name',
    headerName: 'Asset Record Type',
    flex: 1,
    minWidth: 140,
    ...sharedOpts,
  },
  {
    field: 'last_validated_on__c',
    headerName: 'Last Validated On',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value, row) => toSortableTimestamp(row.last_validated_on__c),
    renderCell: ({ row }) => formatSyncedDay(row.last_validated_on__c) ?? '—',
    ...numericOpts,
  },
];

function getRowId(asset: OpportunityRoomAsset) {
  return asset.sfid;
}

export default function AccountAssetsListPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const { data: account } = useGetAccount(accountId);
  const { assets: rows, error, isLoading } = useAccountAssets(accountId);

  return (
    <>
      <Helmet>
        <title>Assets | {account?.name || accountId}</title>
      </Helmet>

      <AccountListPageHeader
        accountId={accountId}
        count={isLoading || error ? undefined : rows.length}
        sortedBy="Asset Record Type"
        title="Assets"
      />

      {error ? (
        <Alert severity="error">Could not load assets: {error.message}</Alert>
      ) : (
        <DataGrid
          autoHeight
          disableRowSelectionOnClick
          columns={COLUMNS}
          getRowId={getRowId}
          loading={isLoading}
          rows={rows}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' } }}
        />
      )}
    </>
  );
}
